import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { profileAPI } from '../../services/api';

// Get initial state
const getInitialState = () => {
  return {
    profile: null,
    businessDetails: null,
    documents: [],
    documentTypes: [],
    bankDetails: null,
    isLoading: false,
    error: null,
  };
};

// ==================== GET PROFILE ====================

export const getProfile = createAsyncThunk(
  'profile/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch profile.'
      );
    }
  }
);

// ==================== GET BUSINESS DETAILS ====================

export const getBusinessDetails = createAsyncThunk(
  'profile/getBusinessDetails',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getBusinessDetails();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch business details.'
      );
    }
  }
);

// ==================== UPDATE BUSINESS DETAILS ====================

export const updateBusinessDetails = createAsyncThunk(
  'profile/updateBusinessDetails',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await profileAPI.updateBusinessDetails(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to update business details.'
      );
    }
  }
);

// ==================== GET DOCUMENT TYPES ====================

export const getDocumentTypes = createAsyncThunk(
  'profile/getDocumentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getDocumentTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch document types.'
      );
    }
  }
);

// ==================== GET DOCUMENTS LIST ====================

export const getDocuments = createAsyncThunk(
  'profile/getDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getDocuments();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch documents.'
      );
    }
  }
);

// ==================== UPLOAD DOCUMENT ====================

export const uploadDocument = createAsyncThunk(
  'profile/uploadDocument',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await profileAPI.uploadDocument(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to upload document.'
      );
    }
  }
);

// ==================== UPLOAD MULTIPLE DOCUMENTS ====================

export const uploadMultipleDocuments = createAsyncThunk(
  'profile/uploadMultipleDocuments',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await profileAPI.uploadMultipleDocuments(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to upload documents.'
      );
    }
  }
);

// ==================== GET BANK DETAILS ====================

export const getBankDetails = createAsyncThunk(
  'profile/getBankDetails',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getBankDetails();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch bank details.'
      );
    }
  }
);

// ==================== CHANGE PASSWORD ====================

export const changePassword = createAsyncThunk(
  'profile/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await profileAPI.changePassword(passwordData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        error.response?.data?.Message ||
        'Failed to change password.'
      );
    }
  }
);

// ==================== PROFILE SLICE ====================

const profileSlice = createSlice({
  name: 'profile',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // GET PROFILE
    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.Profile || action.payload;
        state.error = null;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // GET BUSINESS DETAILS
    builder
      .addCase(getBusinessDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBusinessDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businessDetails = action.payload.BusinessDetails;
        state.error = null;
      })
      .addCase(getBusinessDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // UPDATE BUSINESS DETAILS
    builder
      .addCase(updateBusinessDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBusinessDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businessDetails = action.payload.BusinessDetails;
        state.error = null;
      })
      .addCase(updateBusinessDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // GET DOCUMENT TYPES
    builder
      .addCase(getDocumentTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDocumentTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documentTypes = action.payload.DocumentTypes || [];
        state.error = null;
      })
      .addCase(getDocumentTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // GET DOCUMENTS
    builder
      .addCase(getDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documents = action.payload.Documents || [];
        state.error = null;
      })
      .addCase(getDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.documents = [];
        state.error = action.payload;
      });

    // UPLOAD DOCUMENT
    builder
      .addCase(uploadDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // UPLOAD MULTIPLE DOCUMENTS
    builder
      .addCase(uploadMultipleDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadMultipleDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(uploadMultipleDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // GET BANK DETAILS
    builder
      .addCase(getBankDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBankDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankDetails = action.payload.BankDetails;
        state.error = null;
      })
      .addCase(getBankDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CHANGE PASSWORD
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = profileSlice.actions;
export default profileSlice.reducer;
